import { useCallback, useEffect, useMemo, useState } from 'react';

import { ThemeContext } from './theme-context.js';
import { notifyUser } from '../../ui/ToastViewport.jsx';
import { useSystemColorScheme } from './use-system-color-scheme.js';
import {
  applyThemeToDocument,
  readThemePreference,
  resolveTheme,
  writeThemePreference,
} from './theme-preference.js';

/**
 * Owns the theme preference for this device and applies it to the document.
 *
 * The old implementation called `applyTheme(state.theme)` inside App's render
 * body, mutating a module-level palette object that eleven components read
 * during their own render without subscribing to it. That is a side effect during
 * render, and it broke under memoisation, portals, a second root, StrictMode's
 * double render, and any concurrent interruption.
 *
 * Here the only side effect is in an effect, the only consumer contract is
 * context, and the actual colours are carried by CSS custom properties, so a
 * theme change costs no React work below this provider.
 *
 * Audit references prevented here: theme.js #1, theme.js #2, App.jsx #10.
 *
 * @param {{children: import('react').ReactNode}} props
 */
export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(readThemePreference);
  const systemPrefersDark = useSystemColorScheme();
  const resolvedTheme = resolveTheme(preference, systemPrefersDark);

  useEffect(() => {
    applyThemeToDocument(resolvedTheme);
  }, [resolvedTheme]);

  const setThemePreference = useCallback((next) => {
    setPreference(next);
    writeThemePreference(next);
  }, []);

  const toggleTheme = useCallback(() => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    setThemePreference(next);
    notifyUser(`${next === 'dark' ? 'Dark' : 'Light'} mode enabled.`);
  }, [resolvedTheme, setThemePreference]);

  const value = useMemo(
    () => ({ preference, resolvedTheme, setThemePreference, toggleTheme }),
    [preference, resolvedTheme, setThemePreference, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
